import ThemedElement from "shared/components/ThemedElement";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Box,
  CircularProgress,
  Stack,
  Grid,
  Card,
  CardContent,
  Collapse,
  Alert,
  TextField,
  InputAdornment,
  Typography,
  Button,
} from "@mui/material";
import {
  Search,
  Close,
  Router,
  Wifi,
  Fingerprint,
  Computer,
  CheckCircle,
  Error as ErrorIcon,
  PowerSettingsNew,
  Business,
  SettingsInputAntenna,
  Send,
  ExpandMore,
  Numbers,
  Refresh,
  AccessTime,
  FlashOn,
  Bolt,
  Warning,
  Timer,
  Power,
  PowerOff,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import { ReaderDialogHeader, readerDialogPaper } from "./DeviceActionDialog";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import ResetReaderConfirmationDialog from "./ResetReaderConfirmationDialog";
import SendPulseDialog from "./SendPulseDialog";
import SetPowerDialog from "./SetPowerDialog";
import TogglePowerDialog from "./TogglePowerDialog";

// ============================================
// FUNCIONES AUXILIARES
// ============================================

const formatDateTime = (dateString) => {
  if (!dateString) return "Nunca";
  try {
    const date = new Date(dateString);
    if (!Number.isFinite(date.getTime())) return "Sin fecha válida";
    return date.toLocaleDateString("es-ES", {
      timeZone: "America/Bogota",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (error) {
    console.error("Error formateando fecha:", error);
    return dateString;
  }
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return "Nunca";
  try {
    const date = new Date(dateString);
    const now = new Date();
    if (!Number.isFinite(date.getTime())) return "Sin fecha válida";
    const diffMs = now - date;
    if (diffMs < 0) return formatDateTime(dateString);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Hace unos segundos";
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins !== 1 ? "s" : ""}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? "s" : ""}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays !== 1 ? "s" : ""}`;
    return formatDateTime(dateString);
  } catch (error) {
    return dateString;
  }
};

// ============================================
// COMPONENTES DE INTERFAZ
// ============================================

const ModalHeader = ({ readerGroup, onClose, busy }) => (
  <ReaderDialogHeader
    title={readerGroup.name || "Readers del grupo"}
    subtitle={`Detalle de dispositivos · Último dato: ${formatDateTime(readerGroup.lastUpdate)}`}
    onClose={onClose}
    busy={busy}
  />
);

ModalHeader.propTypes = {
  busy: PropTypes.bool,
  readerGroup: PropTypes.shape({
    name: PropTypes.string,
    totalReaders: PropTypes.number,
    activeReaders: PropTypes.number,
    lastUpdate: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

const GroupStats = ({ readerGroup, commerceStats }) => (
  <>
    <MDTypography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontSize: "1rem" }}>
      <Business sx={{ mr: 1, verticalAlign: "middle", fontSize: 18 }} />
      Resumen del grupo
    </MDTypography>
    <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2.5 }}>
      <StatChip label="Total" value={readerGroup.totalReaders || 0} color="#0288d1" />
      <StatChip
        label="Activos"
        value={readerGroup.activeReaders || 0}
        color={readerGroup.activeReaders > 0 ? "#2e7d32" : "#757575"}
      />
      <StatChip label="Comercios" value={Object.keys(commerceStats).length} color="#7b1fa2" />
    </Box>
  </>
);

GroupStats.propTypes = {
  readerGroup: PropTypes.shape({
    totalReaders: PropTypes.number,
    activeReaders: PropTypes.number,
  }).isRequired,
  commerceStats: PropTypes.object.isRequired,
};

const StatChip = ({ label, value, color }) => (
  <Chip
    label={`${label}: ${value}`}
    sx={{
      color,
      borderColor: color,
      fontWeight: "medium",
      fontSize: "0.8125rem",
      height: 32,
    }}
    variant="outlined"
    size="small"
  />
);

StatChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
};

const ReaderInfo = ({ reader }) => (
  <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 230 } }}>
    <Typography
      variant="body1"
      fontWeight={700}
      sx={{ color: "#243b53", overflowWrap: "anywhere" }}
    >
      {reader.hostname}
    </Typography>
    <Stack direction="row" flexWrap="wrap" gap={1.5} mt={0.5}>
      <Typography variant="caption" sx={{ color: "#526579" }}>
        <Wifi sx={{ fontSize: 13, verticalAlign: "middle", mr: 0.5 }} />
        {reader.ip}
      </Typography>
      <Typography variant="caption" sx={{ color: "#526579" }}>
        {reader.commerce || "Sin comercio"}
      </Typography>
      <Typography variant="caption" sx={{ color: "#526579" }}>
        ID host: {reader.hostId || "Sin información"}
      </Typography>
    </Stack>
    {(reader.more?.resetdate || reader.more?.resetresponse) && (
      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: "#64748b" }}>
        Última operación: {formatDateTime(reader.more.resetdate)} ·{" "}
        {reader.more.resetresponse || "Sin respuesta registrada"}
      </Typography>
    )}
  </Box>
);

ReaderInfo.propTypes = {
  reader: PropTypes.shape({
    commerce: PropTypes.string,
    hostId: PropTypes.string,
    hostname: PropTypes.string,
    ip: PropTypes.string,
    more: PropTypes.shape({
      resetdate: PropTypes.string,
      resetresponse: PropTypes.string,
    }),
  }).isRequired,
};

const ReaderStatusBadge = ({ status, isOnCooldown, cooldownInfo }) => {
  const isActive = Boolean(status);

  if (isOnCooldown && cooldownInfo) {
    return (
      <Tooltip
        title={`Reader en tiempo de espera. Reinicio reciente. Quedan ${cooldownInfo.remainingMinutes} minutos.`}
        arrow
      >
        <Chip
          label={`COOLDOWN: ${cooldownInfo.remainingMinutes}m`}
          size="small"
          sx={{
            backgroundColor: "#ff9800",
            color: "white",
            fontWeight: "bold",
            fontSize: "0.625rem",
            height: 24,
            minWidth: 100,
            "& .MuiChip-label": { px: 1 },
          }}
          icon={<Timer sx={{ fontSize: 12, color: "white" }} />}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={isActive ? "Activo para reinicio automático" : "Inactivo para reinicio automático"}
      arrow
    >
      <Chip
        label={isActive ? "ACTIVO" : "INACTIVO"}
        size="small"
        sx={{
          backgroundColor: isActive ? "#2e7d32" : "#d32f2f",
          color: "white",
          fontWeight: "bold",
          fontSize: "0.625rem",
          height: 24,
          minWidth: 70,
          "& .MuiChip-label": { px: 1 },
        }}
        icon={
          isActive ? (
            <CheckCircle sx={{ fontSize: 12, color: "white" }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 12, color: "white" }} />
          )
        }
      />
    </Tooltip>
  );
};

ReaderStatusBadge.propTypes = {
  status: PropTypes.bool.isRequired,
  isOnCooldown: PropTypes.bool,
  cooldownInfo: PropTypes.shape({
    remainingMinutes: PropTypes.number,
  }),
};

ReaderStatusBadge.defaultProps = {
  isOnCooldown: false,
  cooldownInfo: null,
};

const AntennaItem = ({
  antenna,
  reader,
  sendingPulse,
  gettingPower,
  settingPower,
  gettingStatus,
  togglingPower,
  onOpenPulseDialog,
  onOpenPowerDialog,
  onOpenTogglePowerDialog,
  onRefreshPower,
  onGetStatus,
}) => {
  const isActive = antenna.status === "Activa";
  const unverified = !antenna.status || antenna.status === "Sin verificar";
  const antennaData = antenna.rawData || antenna;
  const antennaMore = antennaData.more || {};

  const isReaderOnCooldown = reader?.isOnCooldown || false;
  const cooldownInfo = reader?.cooldownInfo;
  const hasPulseAPI = reader?.pulse_api || false;

  const powerButtonDisabled =
    !hasPulseAPI || isReaderOnCooldown || settingPower[antenna.id] || gettingPower[antenna.id];

  const pulseButtonDisabled = isReaderOnCooldown || sendingPulse[antenna.id];

  const togglePowerDisabled =
    isReaderOnCooldown ||
    (togglingPower && togglingPower[antenna.id]) ||
    !["Encendida", "Apagada"].includes(antenna.statusFromApi);

  const handleSendPulseClick = (e) => {
    e.stopPropagation();
    if (isReaderOnCooldown) return;
    onOpenPulseDialog(antenna);
  };

  const handlePowerClick = (e) => {
    e.stopPropagation();
    if (isReaderOnCooldown || !hasPulseAPI) return;
    onOpenPowerDialog(antenna);
  };

  const handleTogglePowerClick = (e) => {
    e.stopPropagation();
    if (isReaderOnCooldown) return;
    onOpenTogglePowerDialog(antenna);
  };

  const handleRefreshPower = (e) => {
    e.stopPropagation();
    if (isReaderOnCooldown || !hasPulseAPI || !onRefreshPower || !antenna.id) return;
    onRefreshPower(antenna.id);
  };

  const handleRefreshStatus = (e) => {
    e.stopPropagation();
    if (isReaderOnCooldown || !onGetStatus || !antenna.id) return;
    onGetStatus(antenna.id);
  };

  const getFormattedPower = () => {
    if (antenna.formattedPower) return antenna.formattedPower;
    if (antenna.power !== null && antenna.power !== undefined)
      return (antenna.power / 100).toFixed(1);
    return "N/A";
  };

  const formattedPower = getFormattedPower();

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 0.75,
        borderLeft: "3px solid",
        borderLeftColor: unverified ? "#94a3b8" : isActive ? "#2e7d32" : "#d32f2f",
        backgroundColor: isReaderOnCooldown ? "#fffbf3" : "#fff",
        "&:hover": {
          boxShadow: 1,
          borderColor: isReaderOnCooldown ? "#ff9800" : "#1976d2",
        },
        opacity: isReaderOnCooldown ? 0.85 : 1,
      }}
    >
      <CardContent sx={{ p: 1.25 }}>
        {isReaderOnCooldown && cooldownInfo && (
          <Alert
            severity="warning"
            icon={<Timer fontSize="small" />}
            sx={{
              mb: 1,
              py: 0.25,
              fontSize: "0.6875rem",
              "& .MuiAlert-icon": { fontSize: 16 },
            }}
          >
            <MDTypography variant="caption" fontWeight="medium">
              Reader en tiempo de espera. Quedan {cooldownInfo.remainingMinutes} minutos.
            </MDTypography>
          </Alert>
        )}

        {!hasPulseAPI && (
          <Alert
            severity="info"
            icon={<FlashOn fontSize="small" />}
            sx={{
              mb: 1,
              py: 0.25,
              fontSize: "0.6875rem",
              "& .MuiAlert-icon": { fontSize: 16 },
            }}
          >
            <MDTypography variant="caption" fontWeight="medium">
              La función de <strong>potencia</strong> está deshabilitada.
            </MDTypography>
          </Alert>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <SettingsInputAntenna
              sx={{
                color: isReaderOnCooldown ? "#ff9800" : "#7b1fa2",
                fontSize: 18,
              }}
            />
            <Box>
              <MDTypography
                variant="body2"
                fontWeight="medium"
                fontSize="0.8125rem"
                color={isReaderOnCooldown ? "text.disabled" : "text.primary"}
              >
                {antennaData.namepoint ||
                  antennaData.point ||
                  `Antena ${antenna.id ?? antennaData.id ?? "Sin información"}`}
              </MDTypography>
              {formattedPower !== "N/A" && (
                <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                  <FlashOn
                    sx={{
                      fontSize: 12,
                      color: isReaderOnCooldown ? "#9e9e9e" : "#ff9800",
                    }}
                  />
                  <MDTypography
                    variant="caption"
                    color={isReaderOnCooldown ? "#9e9e9e" : "#ff9800"}
                    fontWeight="medium"
                  >
                    {formattedPower} dBm
                  </MDTypography>
                </Box>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={0.75}>
            <Chip
              label={unverified ? "Sin verificar" : isActive ? "Activa" : "Inactiva"}
              size="small"
              sx={{
                color: unverified ? "#526579" : isActive ? "#2e7d32" : "#d32f2f",
                backgroundColor: unverified
                  ? "#f1f5f9"
                  : isActive
                  ? "rgba(46, 125, 50, 0.08)"
                  : "rgba(211, 47, 47, 0.08)",
                fontWeight: "medium",
                fontSize: "0.625rem",
                height: 20,
                opacity: isReaderOnCooldown ? 0.7 : 1,
              }}
            />
            <Chip
              label={`ID: ${antenna.id ?? antennaData.id ?? "Sin información"}`}
              size="small"
              sx={{
                color: "#1976d2",
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                fontSize: "0.625rem",
                height: 20,
                opacity: isReaderOnCooldown ? 0.7 : 1,
              }}
              icon={<Numbers sx={{ fontSize: 10 }} />}
            />
          </Box>
        </Box>

        <Grid container spacing={0.75} mb={0.75}>
          {antennaData.idhost && (
            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                color={isReaderOnCooldown ? "text.disabled" : "secondary"}
                display="block"
                fontSize="0.6875rem"
              >
                <strong>ID Host:</strong> {antennaData.idhost}
              </MDTypography>
            </Grid>
          )}
          {antennaData.point && (
            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                color={isReaderOnCooldown ? "text.disabled" : "secondary"}
                display="block"
                fontSize="0.6875rem"
              >
                <strong>Punto:</strong> {antennaData.point}
              </MDTypography>
            </Grid>
          )}
          {antennaData.namepoint && (
            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                color={isReaderOnCooldown ? "text.disabled" : "secondary"}
                display="block"
                fontSize="0.6875rem"
              >
                <strong>Nombre:</strong> {antennaData.namepoint}
              </MDTypography>
            </Grid>
          )}
          {formattedPower !== "N/A" && (
            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                color={isReaderOnCooldown ? "text.disabled" : "secondary"}
                display="block"
                fontSize="0.6875rem"
              >
                <strong>Potencia:</strong> {formattedPower} dBm
                {!isReaderOnCooldown && hasPulseAPI && (
                  <Tooltip title="Actualizar potencia">
                    <IconButton
                      size="small"
                      sx={{ ml: 0.5, p: 0, minWidth: 20, height: 20 }}
                      onClick={handleRefreshPower}
                      disabled={gettingPower && gettingPower[antenna.id]}
                    >
                      {gettingPower && gettingPower[antenna.id] ? (
                        <CircularProgress size={10} />
                      ) : (
                        <Refresh sx={{ fontSize: 10 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </MDTypography>
            </Grid>
          )}

          <Grid item xs={12}>
            <MDTypography
              variant="caption"
              color={isReaderOnCooldown ? "text.disabled" : "secondary"}
              display="block"
              fontSize="0.6875rem"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <strong>Estado:</strong>
              {gettingStatus && gettingStatus[antenna.id] ? (
                <CircularProgress size={12} sx={{ ml: 0.5 }} />
              ) : (
                <>
                  <Chip
                    label={antenna.statusFromApi || "Sin verificar"}
                    size="small"
                    icon={
                      antenna.statusFromApi === "Encendida" ? (
                        <Power sx={{ fontSize: 10 }} />
                      ) : antenna.statusFromApi === "Apagada" ? (
                        <PowerOff sx={{ fontSize: 10 }} />
                      ) : (
                        <AccessTime sx={{ fontSize: 10 }} />
                      )
                    }
                    sx={{
                      height: 18,
                      fontSize: "0.55rem",
                      ml: 0.5,
                      backgroundColor:
                        antenna.statusFromApi === "Encendida"
                          ? "rgba(46, 125, 50, 0.1)"
                          : antenna.statusFromApi === "Apagada"
                          ? "rgba(211, 47, 47, 0.1)"
                          : "rgba(158, 158, 158, 0.1)",
                      color:
                        antenna.statusFromApi === "Encendida"
                          ? "#2e7d32"
                          : antenna.statusFromApi === "Apagada"
                          ? "#d32f2f"
                          : "#757575",
                      borderColor:
                        antenna.statusFromApi === "Encendida"
                          ? "#2e7d32"
                          : antenna.statusFromApi === "Apagada"
                          ? "#d32f2f"
                          : "#bdbdbd",
                    }}
                    variant="outlined"
                  />
                  {!isReaderOnCooldown && onGetStatus && (
                    <Tooltip title="Verificar estado de encendido">
                      <IconButton
                        size="small"
                        sx={{ ml: 0.5, p: 0, minWidth: 20, height: 20 }}
                        onClick={handleRefreshStatus}
                        disabled={gettingStatus && gettingStatus[antenna.id]}
                      >
                        <Refresh sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </MDTypography>
          </Grid>

          {antennaMore.resetdate && (
            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                color={isReaderOnCooldown ? "text.disabled" : "secondary"}
                display="block"
                fontSize="0.6875rem"
              >
                <strong>Último pulso:</strong> {formatRelativeTime(antennaMore.resetdate)}
              </MDTypography>
            </Grid>
          )}
          {antennaMore.resetresponse && (
            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                color={isReaderOnCooldown ? "text.disabled" : "secondary"}
                display="block"
                fontSize="0.6875rem"
              >
                <strong>Respuesta:</strong> {antennaMore.resetresponse}
              </MDTypography>
            </Grid>
          )}
        </Grid>

        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          justifyContent="flex-end"
          alignItems="center"
          pt={0.75}
          sx={{ borderTop: "1px solid #e0e0e0" }}
        >
          <Box
            sx={{ width: "100%", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography variant="caption" sx={{ color: "#526579", mr: "auto" }}>
              Consultar
            </Typography>
            <Button
              size="small"
              onClick={handleRefreshStatus}
              disabled={isReaderOnCooldown || Boolean(gettingStatus[antenna.id])}
              startIcon={<Refresh fontSize="small" />}
            >
              Estado
            </Button>
            {hasPulseAPI && (
              <Button
                size="small"
                onClick={handleRefreshPower}
                disabled={isReaderOnCooldown || Boolean(gettingPower[antenna.id])}
                startIcon={<FlashOn fontSize="small" />}
              >
                Potencia actual
              </Button>
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              borderTop: "1px solid #e2e8f0",
              pt: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: "#526579", mr: "auto" }}>
              Acciones
            </Typography>
            {/* BOTÓN ENCENDER / APAGAR - Ahora a la izquierda */}
            <Tooltip
              title={
                isReaderOnCooldown
                  ? `Reader en tiempo de espera. Espere ${
                      cooldownInfo?.remainingMinutes || 5
                    } minutos.`
                  : antenna.statusFromApi === "Encendida"
                  ? "Apagar antena"
                  : antenna.statusFromApi === "Apagada"
                  ? "Encender antena"
                  : "Consulta primero el estado de la antena"
              }
              arrow
            >
              <span>
                <MDButton
                  size="small"
                  variant="outlined"
                  startIcon={
                    togglingPower && togglingPower[antenna.id] ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : antenna.statusFromApi === "Encendida" ? (
                      <PowerOff fontSize="small" />
                    ) : (
                      <PowerSettingsNew fontSize="small" />
                    )
                  }
                  onClick={handleTogglePowerClick}
                  disabled={togglePowerDisabled}
                  sx={{
                    textTransform: "none",
                    fontWeight: "medium",
                    minWidth: "110px",
                    fontSize: "0.6875rem",
                    py: 0.25,
                    px: 1,
                    borderColor: isReaderOnCooldown
                      ? "#bdbdbd"
                      : antenna.statusFromApi === "Encendida"
                      ? "#d32f2f"
                      : "#2e7d32",
                    color: isReaderOnCooldown
                      ? "#bdbdbd"
                      : antenna.statusFromApi === "Encendida"
                      ? "#d32f2f"
                      : "#2e7d32",
                    "&:hover": !isReaderOnCooldown && {
                      borderColor: antenna.statusFromApi === "Encendida" ? "#b71c1c" : "#1b5e20",
                      backgroundColor:
                        antenna.statusFromApi === "Encendida"
                          ? "rgba(211, 47, 47, 0.04)"
                          : "rgba(46, 125, 50, 0.04)",
                    },
                  }}
                >
                  {togglingPower && togglingPower[antenna.id]
                    ? "Procesando..."
                    : antenna.statusFromApi === "Encendida"
                    ? "Apagar"
                    : "Encender"}
                </MDButton>
              </span>
            </Tooltip>

            {/* BOTÓN POTENCIA */}
            {hasPulseAPI && (
              <Tooltip
                title={
                  isReaderOnCooldown
                    ? `Reader en tiempo de espera. Espere ${
                        cooldownInfo?.remainingMinutes || 5
                      } minutos.`
                    : "Configurar potencia de la antena"
                }
                arrow
              >
                <span>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={
                      settingPower && settingPower[antenna.id] ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Bolt fontSize="small" />
                      )
                    }
                    onClick={handlePowerClick}
                    disabled={powerButtonDisabled}
                    sx={{
                      textTransform: "none",
                      fontWeight: "medium",
                      minWidth: "110px",
                      fontSize: "0.6875rem",
                      py: 0.25,
                      px: 1,
                      borderColor: isReaderOnCooldown ? "#bdbdbd" : "#ff9800",
                      color: isReaderOnCooldown ? "#bdbdbd" : "#ff9800",
                      "&:hover": !isReaderOnCooldown && {
                        borderColor: "#f57c00",
                        backgroundColor: "rgba(255, 152, 0, 0.04)",
                      },
                    }}
                  >
                    {settingPower && settingPower[antenna.id] ? "Configurando..." : "Potencia"}
                  </MDButton>
                </span>
              </Tooltip>
            )}

            {/* BOTÓN ENVIAR PULSO */}
            <Tooltip
              title={
                isReaderOnCooldown
                  ? `Reader en tiempo de espera. Espere ${
                      cooldownInfo?.remainingMinutes || 5
                    } minutos.`
                  : "Enviar pulso a la antena"
              }
              arrow
            >
              <span>
                <MDButton
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={
                    sendingPulse && sendingPulse[antenna.id] ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Send fontSize="small" />
                    )
                  }
                  onClick={handleSendPulseClick}
                  disabled={pulseButtonDisabled}
                  sx={{
                    textTransform: "none",
                    fontWeight: "medium",
                    minWidth: "100px",
                    fontSize: "0.6875rem",
                    py: 0.25,
                    px: 1,
                    backgroundColor: isReaderOnCooldown ? "#bdbdbd" : "#1976d2",
                    "&:hover": !isReaderOnCooldown && {
                      backgroundColor: "#1565c0",
                    },
                  }}
                >
                  {sendingPulse && sendingPulse[antenna.id] ? "Enviando..." : "Enviar Pulso"}
                </MDButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

AntennaItem.propTypes = {
  antenna: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    power: PropTypes.number,
    formattedPower: PropTypes.string,
    statusFromApi: PropTypes.string,
    rawData: PropTypes.shape({
      more: PropTypes.shape({
        resetdate: PropTypes.string,
        resetresponse: PropTypes.string,
      }),
    }),
  }).isRequired,
  reader: PropTypes.shape({
    isOnCooldown: PropTypes.bool,
    cooldownInfo: PropTypes.shape({
      remainingMinutes: PropTypes.number,
    }),
    pulse_api: PropTypes.bool,
  }),
  sendingPulse: PropTypes.object,
  gettingPower: PropTypes.object,
  settingPower: PropTypes.object,
  gettingStatus: PropTypes.object,
  togglingPower: PropTypes.object,
  onOpenPulseDialog: PropTypes.func.isRequired,
  onOpenPowerDialog: PropTypes.func.isRequired,
  onOpenTogglePowerDialog: PropTypes.func.isRequired,
  onRefreshPower: PropTypes.func,
  onGetStatus: PropTypes.func,
};

AntennaItem.defaultProps = {
  reader: null,
  sendingPulse: {},
  gettingPower: {},
  settingPower: {},
  gettingStatus: {},
  togglingPower: {},
  onRefreshPower: null,
  onGetStatus: null,
};

const AntennasSection = ({
  antennas,
  reader,
  sendingPulse,
  gettingPower,
  settingPower,
  gettingStatus,
  togglingPower,
  onOpenPulseDialog,
  onOpenPowerDialog,
  onOpenTogglePowerDialog,
  onRefreshPower,
  onGetStatus,
}) => {
  if (!antennas || antennas.length === 0) {
    return (
      <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: "2px solid #e0e0e0" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="subtitle2" color="primary" fontWeight="medium" fontSize="0.875rem">
            <SettingsInputAntenna sx={{ mr: 0.75, verticalAlign: "middle", fontSize: 16 }} />
            Antenas (0)
          </MDTypography>
        </Box>
        <MDTypography
          variant="caption"
          color="secondary"
          fontSize="0.6875rem"
          sx={{ display: "block", textAlign: "center", py: 2 }}
        >
          Este reader no tiene antenas configuradas
        </MDTypography>
      </Box>
    );
  }

  const activeAntennas = antennas.filter((a) => a.status === "Activa").length;
  const antennasWithPower = antennas.filter((a) => a.power || a.formattedPower).length;
  const hasPulseAPI = reader?.pulse_api || false;

  return (
    <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: "2px solid #e0e0e0" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="subtitle2" color="primary" fontWeight="medium" fontSize="0.875rem">
          <SettingsInputAntenna sx={{ mr: 0.75, verticalAlign: "middle", fontSize: 16 }} />
          Antenas ({antennas.length})
        </MDTypography>
        <MDTypography variant="caption" color="secondary" fontSize="0.6875rem">
          {activeAntennas} activas • {antennasWithPower} con potencia configurada
          {reader?.isOnCooldown && (
            <>
              {" • "}
              <Warning sx={{ fontSize: 10, verticalAlign: "middle", color: "#ff9800" }} />
              <ThemedElement component="span" style={{ color: "#ff9800", fontWeight: "medium" }}>
                Reader en tiempo de espera
              </ThemedElement>
            </>
          )}
          {!hasPulseAPI && (
            <>
              {" • "}
              <FlashOn sx={{ fontSize: 10, verticalAlign: "middle", color: "#757575" }} />
              <ThemedElement component="span" style={{ color: "#757575", fontWeight: "medium" }}>
                Sin control de potencia
              </ThemedElement>
            </>
          )}
        </MDTypography>
      </Box>

      {antennas.map((antenna, index) => (
        <AntennaItem
          key={antenna.id || index}
          antenna={antenna}
          reader={reader}
          sendingPulse={sendingPulse}
          gettingPower={gettingPower}
          settingPower={settingPower}
          gettingStatus={gettingStatus}
          togglingPower={togglingPower}
          onOpenPulseDialog={onOpenPulseDialog}
          onOpenPowerDialog={onOpenPowerDialog}
          onOpenTogglePowerDialog={onOpenTogglePowerDialog}
          onRefreshPower={onRefreshPower}
          onGetStatus={onGetStatus}
        />
      ))}
    </Box>
  );
};

AntennasSection.propTypes = {
  antennas: PropTypes.array.isRequired,
  reader: PropTypes.shape({
    isOnCooldown: PropTypes.bool,
    pulse_api: PropTypes.bool,
  }),
  sendingPulse: PropTypes.object,
  gettingPower: PropTypes.object,
  settingPower: PropTypes.object,
  gettingStatus: PropTypes.object,
  togglingPower: PropTypes.object,
  onOpenPulseDialog: PropTypes.func.isRequired,
  onOpenPowerDialog: PropTypes.func.isRequired,
  onOpenTogglePowerDialog: PropTypes.func.isRequired,
  onRefreshPower: PropTypes.func,
  onGetStatus: PropTypes.func,
};

AntennasSection.defaultProps = {
  reader: null,
  sendingPulse: {},
  gettingPower: {},
  settingPower: {},
  gettingStatus: {},
  togglingPower: {},
  onRefreshPower: null,
  onGetStatus: null,
};

const ReaderItem = ({
  reader,
  isExpanded,
  selectedReader,
  loading,
  resetIndividualLoading,
  onToggleExpand,
  onOpenResetDialog,
  sendingPulse,
  gettingPower,
  settingPower,
  gettingStatus,
  togglingPower,
  onOpenPulseDialog,
  onOpenPowerDialog,
  onOpenTogglePowerDialog,
  onRefreshPower,
  onGetStatus,
}) => {
  const readerAntennas = reader.antennas || [];
  const activeAntennas = readerAntennas.filter((a) => a.status === "Activa").length;
  const hasPulseAPI = reader?.pulse_api || false;

  return (
    <Card
      sx={{
        mb: 1.5,
        border: "1px solid #dce5ed",
        borderLeft: "4px solid",
        borderLeftColor: reader.isOnCooldown ? "#d59a3d" : isExpanded ? "#35617d" : "#dce5ed",
        backgroundColor: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: 1,
          backgroundColor: reader.isOnCooldown
            ? "rgba(255, 152, 0, 0.04)"
            : reader.status
            ? "rgba(46, 125, 50, 0.04)"
            : "rgba(0, 0, 0, 0.01)",
        },
      }}
    >
      <CardContent sx={{ p: 1.5, pb: isExpanded ? 0.75 : 1.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1.5}
          sx={{ cursor: "pointer" }}
          onClick={() => onToggleExpand(reader.id)}
        >
          <ReaderInfo reader={reader} />

          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <ReaderStatusBadge
              status={reader.status}
              isOnCooldown={reader.isOnCooldown}
              cooldownInfo={reader.cooldownInfo}
            />

            {!hasPulseAPI && (
              <Chip
                label="Sin control de potencia"
                size="small"
                sx={{
                  backgroundColor: "#757575",
                  color: "white",
                  fontWeight: "medium",
                  fontSize: "0.625rem",
                  height: 24,
                  minWidth: 90,
                  "& .MuiChip-label": { px: 1 },
                }}
                icon={<FlashOn sx={{ fontSize: 12, color: "white" }} />}
              />
            )}

            <MDButton
              size="small"
              variant="outlined"
              color="warning"
              startIcon={
                selectedReader?.id === reader.id && resetIndividualLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <Refresh fontSize="small" />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                onOpenResetDialog(reader);
              }}
              disabled={loading || resetIndividualLoading || reader.isOnCooldown}
              sx={{
                minWidth: 100,
                fontWeight: "medium",
                textTransform: "none",
                fontSize: "0.75rem",
                py: 0.5,
                px: 1.5,
                borderColor: reader.isOnCooldown ? "#bdbdbd" : "#ff9800",
                color: reader.isOnCooldown ? "#bdbdbd" : "#ff9800",
                "&:hover": !reader.isOnCooldown && {
                  borderColor: "#f57c00",
                  backgroundColor: "rgba(255, 152, 0, 0.04)",
                },
              }}
            >
              {selectedReader?.id === reader.id && resetIndividualLoading
                ? "Procesando..."
                : reader.isOnCooldown
                ? "En espera"
                : "Reiniciar"}
            </MDButton>

            <IconButton
              aria-label={`${isExpanded ? "Ocultar" : "Ver"} antenas de ${reader.hostname}`}
              aria-expanded={isExpanded}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(reader.id);
              }}
              sx={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
                p: 0.5,
                color: reader.isOnCooldown ? "#ff9800" : "inherit",
              }}
            >
              <ExpandMore fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={1.5}
          sx={{ cursor: "pointer" }}
          onClick={() => onToggleExpand(reader.id)}
        >
          <MDTypography
            variant="caption"
            color={reader.isOnCooldown ? "warning" : "secondary"}
            fontSize="0.6875rem"
          >
            <SettingsInputAntenna
              sx={{
                fontSize: 12,
                mr: 0.5,
                verticalAlign: "middle",
                color: reader.isOnCooldown ? "#ff9800" : "inherit",
              }}
            />
            {readerAntennas.length} antenas ({activeAntennas} activas)
            {reader.isOnCooldown && (
              <>
                {" • "}
                <Timer sx={{ fontSize: 10, verticalAlign: "middle" }} />
                <span style={{ fontWeight: "medium" }}>
                  Tiempo de espera: {reader.cooldownInfo?.remainingMinutes || 5} minutos
                </span>
              </>
            )}
            {!hasPulseAPI && (
              <>
                {" • "}
                <FlashOn sx={{ fontSize: 10, verticalAlign: "middle", color: "#757575" }} />
                <ThemedElement component="span" style={{ color: "#757575", fontWeight: "medium" }}>
                  Sin control de potencia
                </ThemedElement>
              </>
            )}
          </MDTypography>
          <MDTypography variant="caption" color="primary" fontSize="0.6875rem">
            {isExpanded ? "Ocultar antenas" : "Ver antenas"}
          </MDTypography>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <AntennasSection
            antennas={readerAntennas}
            reader={reader}
            sendingPulse={sendingPulse}
            gettingPower={gettingPower}
            settingPower={settingPower}
            gettingStatus={gettingStatus}
            togglingPower={togglingPower}
            onOpenPulseDialog={onOpenPulseDialog}
            onOpenPowerDialog={onOpenPowerDialog}
            onOpenTogglePowerDialog={onOpenTogglePowerDialog}
            onRefreshPower={onRefreshPower}
            onGetStatus={onGetStatus}
          />
        </Collapse>
      </CardContent>
    </Card>
  );
};

ReaderItem.propTypes = {
  reader: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.bool,
    isOnCooldown: PropTypes.bool,
    cooldownInfo: PropTypes.shape({
      remainingMinutes: PropTypes.number,
    }),
    commerce: PropTypes.string,
    hostId: PropTypes.string,
    hostname: PropTypes.string,
    ip: PropTypes.string,
    more: PropTypes.shape({
      resetdate: PropTypes.string,
      resetresponse: PropTypes.string,
    }),
    antennas: PropTypes.array,
    pulse_api: PropTypes.bool,
  }).isRequired,
  isExpanded: PropTypes.bool,
  selectedReader: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  loading: PropTypes.bool,
  resetIndividualLoading: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired,
  onOpenResetDialog: PropTypes.func.isRequired,
  sendingPulse: PropTypes.object,
  gettingPower: PropTypes.object,
  settingPower: PropTypes.object,
  gettingStatus: PropTypes.object,
  togglingPower: PropTypes.object,
  onOpenPulseDialog: PropTypes.func.isRequired,
  onOpenPowerDialog: PropTypes.func.isRequired,
  onOpenTogglePowerDialog: PropTypes.func.isRequired,
  onRefreshPower: PropTypes.func,
  onGetStatus: PropTypes.func,
};

ReaderItem.defaultProps = {
  isExpanded: false,
  selectedReader: null,
  loading: false,
  resetIndividualLoading: false,
  sendingPulse: {},
  gettingPower: {},
  settingPower: {},
  gettingStatus: {},
  togglingPower: {},
  onRefreshPower: null,
  onGetStatus: null,
};

// ============================================
// COMPONENTE PRINCIPAL READERS MODAL
// ============================================

const ReadersModal = ({
  open,
  onClose,
  readerGroup,
  loading,
  resetIndividualLoading,
  sendingPulse = {},
  gettingPower = {},
  settingPower = {},
  gettingStatus = {},
  togglingPower = {},
  onResetReader,
  onSendPulse,
  getAntennaPower,
  setAntennaPower,
  getAntennaStatus,
  onGetAntennasStatus,
  onSetPowerStatus,
}) => {
  const [expandedReaderId, setExpandedReaderId] = useState(null);
  const [readerSearch, setReaderSearch] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [pulseDialogOpen, setPulseDialogOpen] = useState(false);
  const [powerDialogOpen, setPowerDialogOpen] = useState(false);
  const [togglePowerDialogOpen, setTogglePowerDialogOpen] = useState(false);
  const [selectedReaderForReset, setSelectedReaderForReset] = useState(null);
  const [selectedAntennaForPulse, setSelectedAntennaForPulse] = useState(null);
  const [selectedAntennaForPower, setSelectedAntennaForPower] = useState(null);
  const [selectedAntennaForToggle, setSelectedAntennaForToggle] = useState(null);

  const [localReaderGroup, setLocalReaderGroup] = useState(readerGroup);

  useEffect(() => {
    setLocalReaderGroup(readerGroup || null);
  }, [readerGroup]);

  useEffect(() => {
    if (!open) {
      setExpandedReaderId(null);
      setReaderSearch("");
      setResetDialogOpen(false);
      setPulseDialogOpen(false);
      setPowerDialogOpen(false);
      setTogglePowerDialogOpen(false);
      setSelectedReaderForReset(null);
      setSelectedAntennaForPulse(null);
      setSelectedAntennaForPower(null);
      setSelectedAntennaForToggle(null);
    }
  }, [open]);

  const handleRefreshPower = useCallback(
    async (antennaId) => {
      if (!antennaId || !getAntennaPower) {
        console.error("❌ No se pudo actualizar potencia: falta ID o función getAntennaPower");
        return {
          success: false,
          error: "Función getAntennaPower no disponible",
        };
      }

      try {
        return await getAntennaPower(antennaId);
      } catch (error) {
        console.error("❌ Error en getAntennaPower:", error);
        return {
          success: false,
          error: error.message || "Error al obtener potencia",
        };
      }
    },
    [getAntennaPower]
  );

  const handleRefreshStatus = useCallback(
    async (antennaId) => {
      if (!antennaId || !getAntennaStatus) {
        console.error("❌ No se pudo actualizar estado: falta ID o función getAntennaStatus");
        return {
          success: false,
          error: "Función getAntennaStatus no disponible",
        };
      }

      try {
        const result = await getAntennaStatus(antennaId);

        if (result.success) {
          setLocalReaderGroup((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              readers: prev.readers.map((reader) => ({
                ...reader,
                antennas: reader.antennas?.map((antenna) =>
                  antenna.id === antennaId.toString()
                    ? { ...antenna, statusFromApi: result.status }
                    : antenna
                ),
              })),
            };
          });
        }

        return result;
      } catch (error) {
        console.error("❌ Error en getAntennaStatus:", error);
        return {
          success: false,
          error: error.message || "Error al obtener estado",
        };
      }
    },
    [getAntennaStatus]
  );

  const sortedReaders = useMemo(() => {
    if (!localReaderGroup?.readers) return [];
    const clean = (value) =>
      String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const query = clean(readerSearch).trim();
    return localReaderGroup.readers
      .filter(
        (reader) =>
          !query ||
          [
            reader.hostname,
            reader.commerce,
            reader.ip,
            reader.id,
            reader.hostId,
            ...(reader.antennas || []).map((antenna) => antenna.name),
          ].some((value) => clean(value).includes(query))
      )
      .sort((a, b) => {
        if (a.isOnCooldown && !b.isOnCooldown) return -1;
        if (!a.isOnCooldown && b.isOnCooldown) return 1;
        if (a.status && !b.status) return -1;
        if (!a.status && b.status) return 1;
        return (a.hostname || "").localeCompare(b.hostname || "");
      });
  }, [localReaderGroup, readerSearch]);

  const commerceStats = useMemo(() => {
    const stats = {};
    if (localReaderGroup?.readers) {
      localReaderGroup.readers.forEach((reader) => {
        const commerce = reader.commerce || "Sin comercio";
        stats[commerce] = (stats[commerce] || 0) + 1;
      });
    }
    return stats;
  }, [localReaderGroup]);

  const findReaderByAntenna = (antennaId) => {
    if (!localReaderGroup?.readers) return null;
    for (const reader of localReaderGroup.readers) {
      const foundAntenna = reader.antennas?.find(
        (antenna) => antenna.id === antennaId.toString() || antenna.id === antennaId
      );
      if (foundAntenna) return reader;
    }
    return null;
  };

  const handleReaderToggle = (readerId) => {
    const isExpanding = expandedReaderId !== readerId;
    setExpandedReaderId(isExpanding ? readerId : null);

    if (isExpanding && onGetAntennasStatus) {
      const reader = localReaderGroup?.readers?.find((r) => r.id === readerId);
      if (reader && reader.antennas && reader.antennas.length > 0) {
        const antennaIds = reader.antennas.map((a) => a.id).filter(Boolean);
        if (antennaIds.length > 0) {
          onGetAntennasStatus(antennaIds);
        }
      }
    }
  };

  const handleOpenResetDialog = (reader) => {
    setSelectedReaderForReset(reader);
    setResetDialogOpen(true);
  };

  const handleOpenPulseDialog = (antenna) => {
    setSelectedAntennaForPulse(antenna);
    setPulseDialogOpen(true);
  };

  const handleOpenPowerDialog = (antenna) => {
    setSelectedAntennaForPower(antenna);
    setPowerDialogOpen(true);
  };

  const handleOpenTogglePowerDialog = (antenna) => {
    setSelectedAntennaForToggle(antenna);
    setTogglePowerDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!selectedReaderForReset) return;
    return onResetReader(selectedReaderForReset);
  };

  const handleConfirmPulse = async (antennaId, duration = null) => {
    if (!antennaId || !onSendPulse) {
      console.error("❌ No se pudo enviar pulso: antennaId o onSendPulse no definidos");
      throw new Error("No se pudo enviar el pulso: configuración inválida");
    }
    try {
      return await onSendPulse(antennaId, duration);
    } catch (error) {
      console.error("❌ Error enviando pulso:", error);
      throw error;
    }
  };

  const handleConfirmPower = async (antennaId, powerValue) => {
    if (!antennaId || !setAntennaPower) {
      console.error("❌ No se pudo configurar potencia: falta función setAntennaPower");
      throw new Error("No se pudo configurar la potencia: configuración inválida");
    }

    try {
      return await setAntennaPower(antennaId, powerValue);
    } catch (error) {
      console.error("❌ Error configurando potencia:", error);
      throw error;
    }
  };

  const handleConfirmTogglePower = async (antennaId, state) => {
    if (!antennaId || !onSetPowerStatus) {
      console.error("❌ No se pudo cambiar estado: falta función onSetPowerStatus");
      throw new Error("No se pudo cambiar el estado: configuración inválida");
    }

    try {
      const result = await onSetPowerStatus(antennaId, state);

      if (result?.success) {
        setLocalReaderGroup((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            readers: prev.readers.map((reader) => ({
              ...reader,
              antennas: reader.antennas?.map((antenna) =>
                antenna.id === antennaId.toString()
                  ? { ...antenna, statusFromApi: result.newStatus }
                  : antenna
              ),
            })),
          };
        });
      }

      return result;
    } catch (error) {
      console.error("❌ Error cambiando estado:", error);
      throw error;
    }
  };

  const actionBusy =
    resetIndividualLoading ||
    [sendingPulse, settingPower, togglingPower].some((operation) =>
      Object.values(operation).some(Boolean)
    );
  const closeMain = () => {
    if (!actionBusy) onClose();
  };
  if (!localReaderGroup) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={closeMain}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { ...readerDialogPaper, minHeight: "min(640px, 85vh)" } }}
      >
        <ModalHeader readerGroup={localReaderGroup} onClose={closeMain} busy={actionBusy} />

        <DialogContent
          dividers
          sx={{ py: 2.5, px: { xs: 1.5, sm: 3 }, flex: 1, bgcolor: "#f4f7fa" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <GroupStats readerGroup={localReaderGroup} commerceStats={commerceStats} />
            <TextField
              label="Buscar en este grupo"
              placeholder="Reader, comercio, IP o ID"
              value={readerSearch}
              onChange={(event) => setReaderSearch(event.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2.5, bgcolor: "#fff" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: readerSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Limpiar búsqueda del grupo"
                      onClick={() => setReaderSearch("")}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <MDTypography variant="subtitle1" fontSize="1rem">
                <Fingerprint sx={{ mr: 0.75, verticalAlign: "middle", fontSize: 18 }} />
                Readers ({sortedReaders.length})
              </MDTypography>
              <MDTypography variant="caption" color="secondary" fontSize="0.6875rem">
                Haz clic en un reader para ver y configurar sus antenas
              </MDTypography>
            </Box>

            {sortedReaders.some((r) => r.isOnCooldown) && (
              <Alert severity="warning" icon={<Timer />} sx={{ mb: 2, fontSize: "0.8125rem" }}>
                <MDTypography variant="body2">
                  Algunos readers están en <strong>tiempo de espera</strong> (5 minutos) después de
                  un reinicio. Durante este periodo, no se pueden enviar pulsos ni configurar
                  potencia en sus antenas.
                </MDTypography>
              </Alert>
            )}

            <Box sx={{ flex: 1, minHeight: 200 }}>
              {!sortedReaders.length && (
                <Typography sx={{ p: 3, textAlign: "center", color: "#526579" }}>
                  No hay readers que coincidan con la búsqueda.
                </Typography>
              )}
              {sortedReaders.map((reader) => (
                <ReaderItem
                  key={reader.id}
                  reader={reader}
                  isExpanded={expandedReaderId === reader.id}
                  selectedReader={
                    localReaderGroup?.readers?.find(
                      (reader) => reader.id === selectedReaderForReset?.id
                    ) || selectedReaderForReset
                  }
                  loading={loading}
                  resetIndividualLoading={resetIndividualLoading}
                  onToggleExpand={handleReaderToggle}
                  onOpenResetDialog={handleOpenResetDialog}
                  onOpenPulseDialog={handleOpenPulseDialog}
                  onOpenPowerDialog={handleOpenPowerDialog}
                  onOpenTogglePowerDialog={handleOpenTogglePowerDialog}
                  onRefreshPower={handleRefreshPower}
                  onGetStatus={handleRefreshStatus}
                  sendingPulse={sendingPulse}
                  gettingPower={gettingPower}
                  settingPower={settingPower}
                  gettingStatus={gettingStatus}
                  togglingPower={togglingPower}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: "divider" }}>
          <MDTypography
            variant="caption"
            color="secondary"
            sx={{ flexGrow: 1 }}
            fontSize="0.6875rem"
          >
            <CheckCircle
              sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle", color: "#2e7d32" }}
            />
            {expandedReaderId
              ? "Mostrando antenas del reader seleccionado"
              : "Seleccione un reader para ver y configurar sus antenas"}
            {sortedReaders.some((r) => r.isOnCooldown) && (
              <>
                {" • "}
                <Warning sx={{ fontSize: 12, verticalAlign: "middle", color: "#ff9800" }} />
                <ThemedElement component="span" style={{ color: "#ff9800" }}>
                  Readers en tiempo de espera
                </ThemedElement>
              </>
            )}
          </MDTypography>
          <MDButton
            onClick={closeMain}
            disabled={actionBusy}
            variant="outlined"
            color="primary"
            sx={{
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "0.75rem",
              py: 0.5,
              px: 1.5,
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>

      <ResetReaderConfirmationDialog
        open={resetDialogOpen}
        onClose={() => {
          setResetDialogOpen(false);
          setSelectedReaderForReset(null);
        }}
        onConfirm={handleConfirmReset}
        selectedReader={
          localReaderGroup?.readers?.find((reader) => reader.id === selectedReaderForReset?.id) ||
          selectedReaderForReset
        }
        resetLoading={resetIndividualLoading}
      />

      <SendPulseDialog
        open={pulseDialogOpen}
        onClose={() => {
          setPulseDialogOpen(false);
          setSelectedAntennaForPulse(null);
        }}
        onConfirm={handleConfirmPulse}
        antenna={selectedAntennaForPulse}
        reader={selectedAntennaForPulse ? findReaderByAntenna(selectedAntennaForPulse.id) : null}
        sendingPulse={selectedAntennaForPulse ? sendingPulse[selectedAntennaForPulse.id] : false}
      />

      {powerDialogOpen && (
        <SetPowerDialog
          open={powerDialogOpen}
          onClose={() => {
            setPowerDialogOpen(false);
            setSelectedAntennaForPower(null);
          }}
          onConfirm={handleConfirmPower}
          onRefreshPower={handleRefreshPower}
          antenna={selectedAntennaForPower}
          reader={selectedAntennaForPower ? findReaderByAntenna(selectedAntennaForPower.id) : null}
          gettingPower={gettingPower}
          settingPower={settingPower}
        />
      )}

      {togglePowerDialogOpen && (
        <TogglePowerDialog
          open={togglePowerDialogOpen}
          onClose={() => {
            setTogglePowerDialogOpen(false);
            setSelectedAntennaForToggle(null);
          }}
          onConfirm={handleConfirmTogglePower}
          antenna={selectedAntennaForToggle}
          reader={
            selectedAntennaForToggle ? findReaderByAntenna(selectedAntennaForToggle.id) : null
          }
          currentStatus={selectedAntennaForToggle?.statusFromApi}
          togglingPower={togglingPower}
        />
      )}
    </>
  );
};

ReadersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  readerGroup: PropTypes.shape({
    name: PropTypes.string,
    totalReaders: PropTypes.number,
    activeReaders: PropTypes.number,
    lastUpdate: PropTypes.string,
    readers: PropTypes.array,
  }),
  loading: PropTypes.bool,
  resetIndividualLoading: PropTypes.bool,
  sendingPulse: PropTypes.object,
  gettingPower: PropTypes.object,
  settingPower: PropTypes.object,
  gettingStatus: PropTypes.object,
  togglingPower: PropTypes.object,
  onResetReader: PropTypes.func.isRequired,
  onSendPulse: PropTypes.func.isRequired,
  getAntennaPower: PropTypes.func,
  setAntennaPower: PropTypes.func,
  getAntennaStatus: PropTypes.func,
  onGetAntennasStatus: PropTypes.func,
  onSetPowerStatus: PropTypes.func,
};

ReadersModal.defaultProps = {
  readerGroup: null,
  loading: false,
  resetIndividualLoading: false,
  sendingPulse: {},
  gettingPower: {},
  settingPower: {},
  gettingStatus: {},
  togglingPower: {},
  getAntennaPower: null,
  setAntennaPower: null,
  getAntennaStatus: null,
  onGetAntennasStatus: null,
  onSetPowerStatus: null,
};

export default ReadersModal;
